import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIndustryTerms } from '@/hooks/useIndustryTerms';
import {
  ChefHat,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Flame,
  Coffee,
  Truck,
  UtensilsCrossed,
  Volume2,
  VolumeX,
  RefreshCw,
  LayoutGrid,
  List,
  Timer,
  Zap,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useKitchenOrders,
  getTimingStatus,
  getElapsedTime,
  type KitchenOrder,
} from '@/hooks/useKitchenOrders';
import { usePermissions } from '@/hooks/usePermissions';
import { LocationSelector } from '@/components/location/LocationSelector';

type ViewMode = 'grid' | 'list';

const orderTypeConfig = {
  dine_in: { label: 'Dine In', icon: Coffee, color: 'text-blue-500' },
  takeout: { label: 'Takeout', icon: UtensilsCrossed, color: 'text-orange-500' },
  delivery: { label: 'Delivery', icon: Truck, color: 'text-purple-500' },
  bar_tab: { label: 'Bar', icon: Coffee, color: 'text-amber-500' },
};

const statusColors = {
  pending: 'bg-yellow-500',
  in_progress: 'bg-blue-500',
  ready: 'bg-green-500',
  completed: 'bg-gray-500',
  recalled: 'bg-orange-500',
};

const timingColors = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
};

const timingGlowColors = {
  green: 'shadow-emerald-500/20',
  yellow: 'shadow-amber-500/30 animate-pulse',
  red: 'shadow-red-500/40 animate-pulse',
};

function OrderCard({
  order,
  onStart,
  onBump,
  onRush,
  isHighlighted,
  canInteract = true,
}: {
  order: KitchenOrder;
  onStart: () => void;
  onBump: () => void;
  onRush: () => void;
  isHighlighted?: boolean;
  canInteract?: boolean;
}) {
  const [elapsed, setElapsed] = useState(getElapsedTime(order.sent_at));
  const [timing, setTiming] = useState(getTimingStatus(order.sent_at));

  // Update timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(getElapsedTime(order.sent_at));
      setTiming(getTimingStatus(order.sent_at));
    }, 1000);

    return () => clearInterval(interval);
  }, [order.sent_at]);

  const orderType = order.order?.order_type || 'dine_in';
  const TypeIcon = orderTypeConfig[orderType]?.icon || Coffee;
  const isRush = order.priority > 0;

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all duration-300',
        'border-2 hover:shadow-xl',
        isHighlighted && 'ring-4 ring-primary ring-offset-2 animate-pulse',
        timing === 'green' && 'border-emerald-500/30',
        timing === 'yellow' && 'border-amber-500/50',
        timing === 'red' && 'border-red-500/70',
        timingGlowColors[timing],
        isRush && 'ring-2 ring-red-500 ring-offset-2 ring-offset-background'
      )}
    >
      {/* Top timing bar */}
      <div className={cn('h-2', timingColors[timing])} />

      {/* Rush indicator */}
      {isRush && (
        <div className="absolute top-4 right-4 z-10">
          <Badge variant="destructive" className="gap-1 animate-pulse">
            <Zap className="h-3 w-3" />
            RUSH
          </Badge>
        </div>
      )}

      <CardHeader className="pb-2 pt-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold font-mono">
                #{order.order?.order_number || '---'}
              </span>
              <Badge
                variant="outline"
                className={cn('gap-1', orderTypeConfig[orderType]?.color)}
              >
                <TypeIcon className="h-3 w-3" />
                {orderTypeConfig[orderType]?.label}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {order.order?.table && (
                <span className="font-medium">Table {order.order.table.name}</span>
              )}
              {order.order?.server && (
                <span>
                  • {order.order.server.first_name} {order.order.server.last_name?.charAt(0)}.
                </span>
              )}
              {order.station && (
                <Badge variant="secondary" className="text-xs">
                  {order.station.name}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Timer */}
        <div
          className={cn(
            'flex items-center justify-center gap-2 py-2 rounded-lg font-mono text-2xl font-bold',
            timing === 'green' && 'bg-emerald-500/10 text-emerald-600',
            timing === 'yellow' && 'bg-amber-500/10 text-amber-600',
            timing === 'red' && 'bg-red-500/10 text-red-600'
          )}
        >
          <Timer className="h-5 w-5" />
          {elapsed}
        </div>

        {/* Items */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {order.items.map((item, index) => (
            <div
              key={index}
              className={cn(
                'p-2 rounded-lg bg-muted/50',
                order.status === 'in_progress' && 'bg-blue-500/10'
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg">{item.quantity}x</span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  {item.modifiers && item.modifiers.length > 0 && (
                    <div className="ml-6 text-sm text-muted-foreground">
                      {item.modifiers.map((mod) => mod.name).join(', ')}
                    </div>
                  )}
                  {item.notes && (
                    <div className="ml-6 text-sm text-amber-600 font-medium mt-1">
                      ⚠ {item.notes}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order notes */}
        {order.order?.notes && (
          <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">{order.order.notes}</p>
            </div>
          </div>
        )}

        {/* Status indicator */}
        <div className="flex items-center gap-2">
          <div className={cn('h-2 w-2 rounded-full', statusColors[order.status])} />
          <span className="text-sm font-medium capitalize">
            {order.status.replace('_', ' ')}
          </span>
        </div>

        {/* Actions - only show if user can interact */}
        {canInteract ? (
          <div className="flex gap-2 pt-2 border-t">
            {order.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1"
                  onClick={onStart}
                >
                  <ChefHat className="h-4 w-4" />
                  Start
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={onBump}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Bump
                </Button>
              </>
            )}
            {order.status === 'in_progress' && (
              <Button
                variant="default"
                size="sm"
                className="w-full gap-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={onBump}
              >
                <CheckCircle2 className="h-4 w-4" />
                Ready / Bump
              </Button>
            )}
            {order.status === 'ready' && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full gap-1"
                onClick={onBump}
              >
                <CheckCircle2 className="h-4 w-4" />
                Complete
              </Button>
            )}
            {!isRush && order.status !== 'ready' && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-100"
                onClick={onRush}
              >
                <Flame className="h-4 w-4" />
                Rush
              </Button>
            )}
          </div>
        ) : (
          <div className="pt-2 border-t text-center">
            <p className="text-xs text-muted-foreground">View only</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AllDayView({ counts }: { counts: Record<string, number> }) {
  const sortedItems = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  if (sortedItems.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No items in queue
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {sortedItems.map(([name, count]) => (
        <div
          key={name}
          className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
        >
          <span className="font-medium truncate">{name}</span>
          <Badge variant="secondary" className="text-lg font-bold ml-2">
            {count}
          </Badge>
        </div>
      ))}
    </div>
  );
}

export default function KDS() {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightOrderId = searchParams.get('orderId');
  const terms = useIndustryTerms();

  const [selectedStation, setSelectedStation] = useState<string | 'all'>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showAllDay, setShowAllDay] = useState(false);

  // Check if user can interact with KDS (not a cashier/employee)
  const { effectiveRole } = usePermissions();
  const isCashier = effectiveRole === 'cashier' || effectiveRole === 'employee';
  const canInteract = !isCashier;

  // Clear highlight after 10 seconds
  useEffect(() => {
    if (highlightOrderId) {
      const timeout = setTimeout(() => {
        setSearchParams({});
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [highlightOrderId, setSearchParams]);

  const {
    orders,
    stations,
    allDayCounts,
    isLoading,
    refetch,
    startOrder,
    bumpOrder,
    completeOrder,
    setPriority,
  } = useKitchenOrders(selectedStation === 'all' ? null : selectedStation);

  // Group orders by status
  const pendingOrders = orders.filter((o) => o.status === 'pending');
  const inProgressOrders = orders.filter((o) => o.status === 'in_progress');
  const readyOrders = orders.filter((o) => o.status === 'ready');

  const handleBump = (order: KitchenOrder) => {
    if (order.status === 'pending' || order.status === 'in_progress') {
      bumpOrder.mutate({ kitchenOrderId: order.id });
    } else if (order.status === 'ready') {
      completeOrder.mutate({ kitchenOrderId: order.id });
    }
  };

  const handleStart = (order: KitchenOrder) => {
    startOrder.mutate({ kitchenOrderId: order.id });
  };

  const handleRush = (order: KitchenOrder) => {
    setPriority.mutate({ kitchenOrderId: order.id, priority: 1 });
  };

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-orange-500/10">
          <ChefHat className="h-6 w-6 text-orange-500" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            {terms.kitchenDisplay}
          </h1>
          <p className="text-sm text-muted-foreground">
            {orders.length} active orders • Real-time updates
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Location selector */}
        <LocationSelector showAllOption={false} />

        {/* Station filter */}
        <Select
          value={selectedStation}
          onValueChange={(value) => setSelectedStation(value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Stations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stations</SelectItem>
            {stations.map((station) => (
              <SelectItem key={station.id} value={station.id}>
                {station.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* View mode toggle */}
        <div className="flex items-center rounded-lg border p-1">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>

        {/* All-day toggle */}
        <Button
          variant={showAllDay ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => setShowAllDay(!showAllDay)}
        >
          All Day
        </Button>

        {/* Sound toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={cn(!soundEnabled && 'text-muted-foreground')}
        >
          {soundEnabled ? (
            <Volume2 className="h-5 w-5" />
          ) : (
            <VolumeX className="h-5 w-5" />
          )}
        </Button>

        {/* Refresh */}
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>

        {/* Live indicator */}
        <Badge variant="secondary" className="gap-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          Live
        </Badge>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <DashboardLayout header={header}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-muted-foreground">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span>Loading kitchen orders...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (showAllDay) {
    return (
      <DashboardLayout header={header}>
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold">All-Day Counts</h2>
            <p className="text-sm text-muted-foreground">
              Total quantities for all active orders
            </p>
          </CardHeader>
          <CardContent>
            <AllDayView counts={allDayCounts} />
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout header={header}>
      {/* Status summary */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/10">
          <Clock className="h-5 w-5 text-yellow-600" />
          <span className="font-medium text-yellow-700">
            {pendingOrders.length} Pending
          </span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10">
          <ChefHat className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-700">
            {inProgressOrders.length} In Progress
          </span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/10">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <span className="font-medium text-green-700">
            {readyOrders.length} Ready
          </span>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
            <ChefHat className="h-10 w-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Active Orders</h3>
          <p className="text-muted-foreground max-w-md">
            When orders are sent to the kitchen, they'll appear here. 
            The display will update automatically in real-time.
          </p>
        </div>
      ) : (
        <div
          className={cn(
            'grid gap-4',
            viewMode === 'grid' &&
              'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
            viewMode === 'list' && 'grid-cols-1 max-w-4xl'
          )}
        >
          {/* Rush orders first */}
          {orders
            .filter((o) => o.priority > 0)
            .map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onStart={() => handleStart(order)}
                onBump={() => handleBump(order)}
                onRush={() => handleRush(order)}
                isHighlighted={order.order_id === highlightOrderId}
                canInteract={canInteract}
              />
            ))}
          {/* Then regular orders sorted by time */}
          {orders
            .filter((o) => o.priority === 0)
            .map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onStart={() => handleStart(order)}
                onBump={() => handleBump(order)}
                onRush={() => handleRush(order)}
                isHighlighted={order.order_id === highlightOrderId}
                canInteract={canInteract}
              />
            ))}
        </div>
      )}
    </DashboardLayout>
  );
}





